import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { Formik, Form } from 'formik';
import {
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Header, Success, Failed } from '../../components';
import { customStyles } from '../../Utils/utils';
import { useSelector, useDispatch } from 'react-redux';
import { AppDispatch, RootState } from 'platform/custom/store/store';
import { changePasswordApi } from '../../rtk-apis/auth/changePassword';
import { clearState } from '../../store/slices/Auth/changePassword';
import { getEmail, getHospitalId } from '../../Utils/authUtils';
import { cardiacTokens as T } from '../../theme/cardiacTheme';

/* ════════════════════════════════════════════════════════════════════════
   Update Password — MUI rewrite on the shared cardiac theme (navy + teal).
   Was legacy dashboard.scss form markup. Logic unchanged: same Formik + Yup
   schema, same live password-criteria checklist, same Save-enable rule.
   ════════════════════════════════════════════════════════════════════════ */

const inputSx = {
  '& .MuiOutlinedInput-root': {
    background: T.surface,
    borderRadius: '10px',
    color: T.ink,
    fontSize: 14,
  },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: T.line },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: T.lineStrong },
  '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: T.teal },
  '& .MuiInputBase-input': { padding: '12px 14px' },
  '& .MuiFormHelperText-root': { color: T.red, ml: '2px', fontSize: 12 },
} as const;

const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.ink2, mb: '8px' }}>
    {children}
    <Box
      component="span"
      sx={{ color: T.red, ml: '2px' }}
    >
      *
    </Box>
  </Typography>
);

// one password-criteria pill: green dot+text when met, muted red when not
const Crit: React.FC<{ ok: boolean; children: React.ReactNode }> = ({ ok, children }) => (
  <Stack
    direction="row"
    alignItems="center"
    gap={0.75}
  >
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: ok ? T.green : T.red,
        flexShrink: 0,
      }}
    />
    <Typography sx={{ fontSize: 12, color: ok ? T.green : T.ink3 }}>{children}</Typography>
  </Stack>
);

const ChangePassword = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const changePasswordResponse = useSelector((state: RootState) => state.changePasswordResponse);
  const userEmail = getEmail();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showFailedModal, setShowFailedModal] = useState(false);
  const [displayMessage, setDisplayMessage] = useState('');
  const [apiRejectedResponse, setApiRejectedResponse] = useState('');

  const [showPassword, setShowPassword] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });
  const [passwordCriteria, setPasswordCriteria] = useState({
    length: false,
    uppercase: false,
    number: false,
  });

  const initialValues = {
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  };

  const validationSchema = Yup.object().shape({
    currentPassword: Yup.string().required('Current Password is required'),
    newPassword: Yup.string()
      .required('New Password is required')
      .test('passwordCriteria', 'Password does not meet criteria', value => {
        const criteria = {
          length: value?.length >= 8 && value?.length <= 20,
          uppercase: /[A-Z]/.test(value),
          number: /\d/.test(value),
        };
        setPasswordCriteria(criteria);
        return Object.values(criteria).every(Boolean);
      }),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('newPassword')], 'Passwords must match')
      .required('Confirm Password is required'),
  });

  const togglePasswordVisibility = (
    field: 'currentPassword' | 'newPassword' | 'confirmPassword'
  ) => {
    setShowPassword(prev => ({ ...prev, [field]: !prev[field] }));
  };

  useEffect(() => {
    if (changePasswordResponse.status === 'SUCCESS') {
      setDisplayMessage('Password updated successfully.');
      setShowSuccessModal(true);
      dispatch(clearState());
    } else if (changePasswordResponse.status === 'ERROR') {
      setApiRejectedResponse(changePasswordResponse.msg || 'Failed to change password.');
      setShowFailedModal(true);
      dispatch(clearState());
    }
  }, [changePasswordResponse, dispatch]);

  const handleFormSubmit = (values: any, { setSubmitting }: any) => {
    if (userEmail) {
      const hospitalId = getHospitalId();
      dispatch(
        changePasswordApi({
          username: userEmail,
          oldPassword: values.currentPassword,
          newPassword: values.newPassword,
          hospitalId,
        })
      )
        .then(() => {
          if (
            changePasswordResponse.status === 'SUCCESS' ||
            changePasswordResponse.status === 'ERROR'
          ) {
            setDisplayMessage(changePasswordResponse.msg);
            setShowSuccessModal(true);
            dispatch(clearState());
          }
        })
        .catch(error => {
          setApiRejectedResponse(error.message || 'An unexpected error occurred');
          setShowFailedModal(true);
        });
    }
    setSubmitting(false);
  };

  // eye-toggle adornment builder for the three password fields
  const eyeAdornment = (field: 'currentPassword' | 'newPassword' | 'confirmPassword') => ({
    startAdornment: (
      <InputAdornment position="start">
        <LockOutlinedIcon sx={{ fontSize: 18, color: T.ink3 }} />
      </InputAdornment>
    ),
    endAdornment: (
      <InputAdornment position="end">
        <IconButton
          onClick={() => togglePasswordVisibility(field)}
          edge="end"
          size="small"
          sx={{ color: T.ink3 }}
        >
          {showPassword[field] ? (
            <VisibilityOff sx={{ fontSize: 18 }} />
          ) : (
            <Visibility sx={{ fontSize: 18 }} />
          )}
        </IconButton>
      </InputAdornment>
    ),
  });

  return (
    <section
      className="layoutContainer"
      style={customStyles.layoutBackground}
    >
      <div className="layoutGrid">
        <div className="page">
          <Header />
          <div className="page__body">
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleFormSubmit}
            >
              {({
                isValid,
                dirty,
                resetForm,
                isSubmitting,
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
              }) => (
                <Form>
                  <Box
                    sx={{
                      fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
                      px: { xs: '18px', md: '30px' },
                      py: '22px',
                      color: T.ink,
                    }}
                  >
                    {/* head: title + Cancel/Save */}
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      flexWrap="wrap"
                      gap={2}
                      sx={{ mb: '20px' }}
                    >
                      <Typography
                        component="h1"
                        sx={{ fontSize: 22, fontWeight: 700, color: '#fff' }}
                      >
                        Update Password
                      </Typography>
                      <Stack
                        direction="row"
                        gap={1.5}
                      >
                        <Button
                          type="button"
                          onClick={() => {
                            resetForm();
                            navigate('/dashboard');
                          }}
                          variant="outlined"
                          sx={{
                            color: T.ink,
                            borderColor: T.lineStrong,
                            borderRadius: '10px',
                            px: '18px',
                            py: '9px',
                            fontWeight: 600,
                            '&:hover': {
                              borderColor: T.teal,
                              color: T.teal,
                              background: 'transparent',
                            },
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          disabled={
                            !isValid ||
                            !dirty ||
                            isSubmitting ||
                            !Object.values(passwordCriteria).every(Boolean)
                          }
                          variant="contained"
                          sx={{
                            color: '#04221d',
                            fontWeight: 700,
                            borderRadius: '10px',
                            px: '20px',
                            py: '9px',
                            background: `linear-gradient(92deg, ${T.sky}, ${T.teal})`,
                            boxShadow: '0 6px 16px -7px rgba(45,212,191,0.7)',
                            '&:hover': {
                              filter: 'brightness(1.08)',
                              background: `linear-gradient(92deg, ${T.sky}, ${T.teal})`,
                            },
                            '&.Mui-disabled': { opacity: 0.5, color: '#04221d' },
                          }}
                        >
                          Save
                        </Button>
                      </Stack>
                    </Stack>

                    {/* password card */}
                    <Box
                      sx={{
                        background: `linear-gradient(180deg, ${T.surface2}, ${T.surface})`,
                        border: `1px solid ${T.line}`,
                        borderRadius: '16px',
                        p: { xs: '18px', md: '26px' },
                      }}
                    >
                      <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#fff', mb: '22px' }}>
                        Password Details
                      </Typography>

                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                          columnGap: '22px',
                          rowGap: '20px',
                          alignItems: 'start',
                        }}
                      >
                        {/* Current Password */}
                        <Box>
                          <Label>Current Password</Label>
                          <TextField
                            fullWidth
                            name="currentPassword"
                            type={showPassword.currentPassword ? 'text' : 'password'}
                            placeholder="Current Password"
                            value={values.currentPassword}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={!!(touched.currentPassword && errors.currentPassword)}
                            helperText={
                              (touched.currentPassword && (errors.currentPassword as string)) || ''
                            }
                            InputProps={eyeAdornment('currentPassword')}
                            sx={inputSx}
                          />
                        </Box>

                        {/* New Password + criteria */}
                        <Box>
                          <Label>New Password</Label>
                          <TextField
                            fullWidth
                            name="newPassword"
                            type={showPassword.newPassword ? 'text' : 'password'}
                            placeholder="New Password"
                            value={values.newPassword}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={!!(touched.newPassword && errors.newPassword)}
                            helperText={
                              (touched.newPassword && (errors.newPassword as string)) || ''
                            }
                            InputProps={eyeAdornment('newPassword')}
                            sx={inputSx}
                          />
                          <Stack
                            direction="row"
                            flexWrap="wrap"
                            alignItems="center"
                            gap={1.75}
                            sx={{ mt: '10px' }}
                          >
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.ink3 }}>
                              Your Password Must Contain:
                            </Typography>
                            <Crit ok={passwordCriteria.length}>Between 8 and 20 characters</Crit>
                            <Crit ok={passwordCriteria.uppercase}>1 upper case letter</Crit>
                            <Crit ok={passwordCriteria.number}>1 or more numbers</Crit>
                          </Stack>
                        </Box>

                        {/* Confirm Password */}
                        <Box>
                          <Label>Confirm Password</Label>
                          <TextField
                            fullWidth
                            name="confirmPassword"
                            type={showPassword.confirmPassword ? 'text' : 'password'}
                            placeholder="Confirm Password"
                            value={values.confirmPassword}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={!!(touched.confirmPassword && errors.confirmPassword)}
                            helperText={
                              (touched.confirmPassword && (errors.confirmPassword as string)) || ''
                            }
                            InputProps={eyeAdornment('confirmPassword')}
                            sx={inputSx}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
      <Success
        handleCloseModal={() => setShowSuccessModal(false)}
        title={displayMessage}
        isSuccessModalOpen={showSuccessModal}
      />
      <Failed
        handleCloseModal={() => setShowFailedModal(false)}
        title={apiRejectedResponse}
        isFailedModalOpen={showFailedModal}
      />
    </section>
  );
};

export default ChangePassword;
